/**
 * Import a publication or a proposal from a Word document (.docx) — or an Otable document (.otable), exported from
 * Otable with its comments and settings — so the co-authors can go on writing it
 * together. Proposals have no bibliography: their citations keep the text Word shows.
 * Step 1 reads the file (nothing is saved) and shows what was found; step 2 imports it.
 * Backend: POST /proposal/proposals/<id>/import_publication_docx/ (docx_import.py).
 */
import React, { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, LinearProgress, Radio, RadioGroup, Stack, Typography,
} from '@mui/material';
import { Description as WordIcon, CloudUpload as UploadIcon } from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';

function SectionTree({ sections, depth = 0 }) {
  return sections.map((s, i) => (
    <Box key={`${depth}-${i}-${s.title}`}>
      <Box display="flex" justifyContent="space-between" gap={2} sx={{ pl: depth * 2.5, py: 0.35 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: depth ? 400 : 600 }} noWrap>{s.title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', flexShrink: 0 }}>{s.words.toLocaleString()} words</Typography>
      </Box>
      {s.children?.length > 0 && <SectionTree sections={s.children} depth={depth + 1} />}
    </Box>
  ));
}

export default function ImportPublicationDialog({ open, onClose, proposalId, onImported, kind = 'publication' }) {
  const isPub = kind === 'publication';
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');                 // 'reading' | 'importing' | 'finishing'
  const [error, setError] = useState('');
  const [mode, setMode] = useState('replace');
  const [useTitle, setUseTitle] = useState(true);
  const [useAbstract, setUseAbstract] = useState(true);
  const [useRefs, setUseRefs] = useState(true);
  const [useAuthors, setUseAuthors] = useState(true);
  const [done, setDone] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setBusy(''); setError(''); setMode('replace'); setDone(null); };
  const close = () => { if (busy) return; reset(); onClose(); };

  const read = async (f) => {
    setFile(f); setError(''); setPreview(null); setBusy('reading');
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('dry_run', '1');
      const res = await axiosService.post(`/proposal/proposals/${proposalId}/import_publication_docx/`, fd);
      setPreview(res.data);
      setUseTitle(Boolean(res.data.title));
      setUseAbstract(Boolean(res.data.abstract) || Boolean(res.data.existing_abstract));
      setUseRefs(res.data.counts.references > 0 || res.data.existing_references > 0);
      setUseAuthors((res.data.authors || []).length > 0);
      setMode('replace');                                // a new version of the paper replaces the old one
    } catch (e) {
      setError(e.response?.data?.error || 'The document could not be read.');
    } finally {
      setBusy('');
    }
  };

  const runImport = async () => {
    setBusy('importing'); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mode', mode);
      fd.append('use_title', useTitle ? '1' : '0');
      fd.append('use_abstract', useAbstract ? '1' : '0');
      fd.append('use_references', useRefs ? '1' : '0');
      fd.append('use_authors', useAuthors ? '1' : '0');
      const res = await axiosService.post(`/proposal/proposals/${proposalId}/import_publication_docx/`, fd);
      setBusy('finishing');
      await onImported?.(res.data);                     // look up references by DOI, rebuild References, reload
      setDone(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'The import did not complete. Nothing was changed.');
    } finally {
      setBusy('');
    }
  };

  const c = preview?.counts;
  const authors = preview?.authors || [];
  const replacing = mode === 'replace' && Boolean(preview && (preview.existing_sections || preview.existing_references
    || preview.existing_abstract || preview.existing_externals));
  const option = (checked, set, label) => (
    <FormControlLabel key={label} control={<Checkbox size="small" checked={checked} onChange={(e) => set(e.target.checked)} />}
      label={<Typography sx={{ fontSize: 14 }}>{label}</Typography>} />
  );
  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WordIcon color="primary" /> Import a {isPub ? 'publication' : 'proposal'} from Word
      </DialogTitle>
      <DialogContent dividers>
        {busy && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {done ? (
          <Alert severity="success">
            Imported {done.sections} section{done.sections === 1 ? '' : 's'}
            {done.figures ? `, ${done.figures} figure${done.figures === 1 ? '' : 's'}` : ''}
            {done.references ? ` and ${done.references} reference${done.references === 1 ? '' : 's'} (${done.cited_references} cited in the text)` : ''}.
            {done.authors ? ` The author list now follows the document (${done.authors} author${done.authors === 1 ? '' : 's'}).` : ''}
            {done.figures_dropped ? ` ${done.figures_dropped} figure(s) were left out: they did not fit in your 5 MB of storage or could not be compressed (Settings → Storage).` : ''}
            {done.comments ? ` ${done.comments} comment thread${done.comments === 1 ? '' : 's'} came with it.` : ''}
            {' '}Your co-authors can now edit it with you.
          </Alert>
        ) : !preview ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 2 }}>
              {isPub
                ? 'Bring in a paper you started in Word: its sections and subsections, text formatting, tables, figures with their captions, the reference list and the in-text citations (numbered, author–year, or from Zotero and Mendeley).'
                : 'Bring in a proposal you started in Word: its sections and subsections, text formatting, tables and figures with their captions.'}
            </Typography>
            <Button variant="contained" component="label" startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />} disabled={Boolean(busy)}>
              {busy ? 'Reading the document…' : 'Choose a .docx or .otable file'}
              <input hidden type="file" accept=".docx,.otable,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.otable.document+zip"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) read(f); }} />
            </Button>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1.5 }}>
              Nothing is saved until you confirm on the next step. Older .doc files: save them as .docx first. An Otable
              document (.otable), exported from Otable, also brings its comments, citation style and journal.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{file?.name}</Typography>
              {preview.title && <Typography sx={{ fontSize: 17, fontWeight: 700, mt: 0.5 }}>{preview.title}</Typography>}
              {authors.length > 0 && (
                <Typography sx={{ fontSize: 13.5, mt: 0.5 }}>
                  {authors.map((a, i) => (
                    <React.Fragment key={`${a.name}-${i}`}>
                      {i > 0 && ', '}
                      <Box component="span" title={[a.affiliation, a.email].filter(Boolean).join(' · ') || undefined}>
                        {a.name}{a.is_corresponding ? '*' : ''}
                      </Box>
                    </React.Fragment>
                  ))}
                </Typography>
              )}
              {preview.abstract && (
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: 0.5 }}>
                  {preview.abstract.length > 280 ? `${preview.abstract.slice(0, 280)}…` : preview.abstract}
                </Typography>
              )}
            </Box>
            <Box display="flex" flexWrap="wrap" gap={0.75}>
              {[[c.sections, 'sections'], [c.words, 'words'], [c.tables, 'tables'], [c.figures, 'figures'],
                ...(isPub ? [[c.references, 'references'], [c.citations, 'in-text citations linked']] : [])].map(([v, l]) => (
                <Chip key={l} size="small" variant="outlined" label={`${(v || 0).toLocaleString()} ${l}`} />
              ))}
            </Box>
            {preview.sections.length > 0 && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, maxHeight: 220, overflowY: 'auto' }}>
                <SectionTree sections={preview.sections} />
              </Box>
            )}
            {preview.warnings.map((w) => <Alert key={w} severity="info" sx={{ py: 0 }}>{w}</Alert>)}

            {preview.existing_sections > 0 && (
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>This {isPub ? 'publication' : 'proposal'} already has {preview.existing_sections} section{preview.existing_sections === 1 ? '' : 's'}</Typography>
                <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
                  <FormControlLabel value="replace" control={<Radio size="small" />}
                    label="Replace it with the document: its sections (with their text and comments), figures and the fields ticked below" />
                  <FormControlLabel value="append" control={<Radio size="small" />} label="Keep it and add the imported sections after it" />
                </RadioGroup>
              </Box>
            )}
            <Stack>
              {replacing && <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>Untick what you want to keep as it is.</Typography>}
              {preview.title && option(useTitle, setUseTitle, replacing ? 'Replace the title' : "Use the document's title")}
              {isPub && authors.length > 0 && option(useAuthors, setUseAuthors, replacing
                ? `Replace the author list with the document's ${authors.length}${preview.existing_externals ? ` (the current ${preview.existing_externals} external author${preview.existing_externals === 1 ? ' is' : 's are'} removed)` : ''}; consortium members stay`
                : `Use the document's author list (${authors.length}); names not on Otable are added as external authors`)}
              {(preview.abstract || (replacing && preview.existing_abstract)) && option(useAbstract, setUseAbstract, replacing
                ? (preview.abstract ? 'Replace the abstract and keywords' : 'Clear the abstract and keywords (the document has none)')
                : 'Use its abstract')}
              {isPub && (c.references > 0 || (replacing && preview.existing_references > 0)) && option(useRefs, setUseRefs, replacing
                ? (c.references > 0
                  ? `Replace the bibliography${preview.existing_references ? ` (the current ${preview.existing_references} references)` : ''} with the document's ${c.references}`
                  : `Clear the bibliography (the document has no reference list; ${preview.existing_references} references now)`)
                : 'Import the references and link the citations')}
            </Stack>
            {busy === 'finishing' && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                {isPub ? 'Looking up references by DOI and building the References section…' : 'Finishing…'}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {done ? (
          <Button variant="contained" onClick={close}>Done</Button>
        ) : (
          <>
            {preview && !busy && <Button onClick={() => setPreview(null)}>Choose another file</Button>}
            <Box flex={1} />
            <Button onClick={close} disabled={Boolean(busy)}>Cancel</Button>
            {preview && (
              <Button variant="contained" onClick={runImport} disabled={Boolean(busy) || (!c.sections && !preview.abstract)}
                color={mode === 'replace' && preview.existing_sections ? 'warning' : 'primary'}>
                {mode === 'replace' && preview.existing_sections ? 'Replace and import' : 'Import'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
